import { useState } from 'react';
import { createPortal } from 'react-dom';
import { reactivateInitiative } from '@/lib/api/initiatives';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type { Initiative } from '@/types/initiative';

interface ReactivateInitiativeModalProps {
  initiative: Initiative;
  locale: Locale;
  onSuccess: () => void;
  onClose: () => void;
}

function today() {
  return new Date().toISOString().split('T')[0] ?? '';
}

export function ReactivateInitiativeModal({
  initiative,
  locale,
  onSuccess,
  onClose,
}: ReactivateInitiativeModalProps) {
  const isRTL = locale === 'ar';
  const [startDate, setStartDate] = useState(today());
  const [isOpenEnded, setIsOpenEnded] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [usePrevSettings, setUsePrevSettings] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = startDate && (isOpenEnded || endDate);

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError(t(locale, 'requiredField'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await reactivateInitiative(initiative.id, {
        initiative_id: initiative.id,
        start_date: startDate,
        end_date: isOpenEnded ? null : endDate,
        use_previous_settings: usePrevSettings,
      });
      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || t(locale, 'error'));
      }
    } catch {
      setError(t(locale, 'error'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-card border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none text-sm';

  const content = (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reactivate-modal-title"
    >
      <div className="bg-card w-full max-w-[480px] rounded-3xl shadow-2xl overflow-hidden">
        <div className="p-8">
          {/* Title */}
          <h2
            id="reactivate-modal-title"
            className={`text-2xl font-bold text-gray-900 mb-8 ${isRTL ? 'text-right' : ''}`}
          >
            {t(locale, 'reactivateInitiative')}
          </h2>

          <div className="space-y-6">
            {/* Start Date */}
            <div>
              <label
                htmlFor="reactivate-start-date"
                className={`block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 ${isRTL ? 'text-right' : ''}`}
              >
                {t(locale, 'startDate')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="reactivate-start-date"
                  type="date"
                  value={startDate}
                  min={today()}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={`${inputClass} dir-ltr`}
                  autoFocus
                />
                <span className="material-icons-outlined absolute top-1/2 -translate-y-1/2 end-3 text-gray-400 pointer-events-none">
                  calendar_today
                </span>
              </div>
            </div>

            {/* Open-Ended Toggle */}
            <div
              className={`flex items-center gap-3 cursor-pointer group ${isRTL ? 'flex-row-reverse justify-end' : 'justify-end'}`}
              onClick={() => setIsOpenEnded(!isOpenEnded)}
              role="checkbox"
              aria-checked={isOpenEnded}
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setIsOpenEnded(!isOpenEnded)}
            >
              <span className={`text-sm font-medium text-gray-600 dark:text-gray-300 ${isRTL ? 'text-right' : ''}`}>
                {t(locale, 'setOpenEnded')}
              </span>
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  isOpenEnded
                    ? 'bg-primary border-primary'
                    : 'border-gray-300 dark:border-gray-600 group-hover:border-primary'
                }`}
              >
                {isOpenEnded && (
                  <span className="material-icons-outlined text-[12px] text-white">check</span>
                )}
              </div>
            </div>

            {/* End Date (hidden if open-ended) */}
            {!isOpenEnded && (
              <div>
                <label
                  htmlFor="reactivate-end-date"
                  className={`block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 ${isRTL ? 'text-right' : ''}`}
                >
                  {t(locale, 'endDate')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="reactivate-end-date"
                    type="date"
                    value={endDate}
                    min={startDate || today()}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder="mm/dd/yyyy"
                    className={`${inputClass} dir-ltr`}
                  />
                  <span className="material-icons-outlined absolute top-1/2 -translate-y-1/2 end-3 text-gray-400 pointer-events-none">
                    calendar_today
                  </span>
                </div>
              </div>
            )}

            {/* Use Previous Settings Checkbox */}
            <div className={`flex items-start gap-3 pt-2 ${isRTL ? 'flex-row-reverse justify-end' : 'justify-end'}`}>
              <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
                <label
                  className={`flex items-center gap-3 cursor-pointer ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
                  htmlFor="use-prev-settings"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t(locale, 'usePreviousSettings')}
                  </span>
                  <div
                    id="use-prev-settings"
                    role="checkbox"
                    aria-checked={usePrevSettings}
                    tabIndex={0}
                    onClick={() => setUsePrevSettings(!usePrevSettings)}
                    onKeyDown={(e) => e.key === 'Enter' && setUsePrevSettings(!usePrevSettings)}
                    className={`w-5 h-5 rounded-full flex items-center justify-center cursor-pointer transition-colors ${
                      usePrevSettings
                        ? 'bg-primary'
                        : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {usePrevSettings && (
                      <span className="material-icons-outlined text-[12px] text-white font-bold">check</span>
                    )}
                  </div>
                </label>
                <p className={`text-[11px] text-gray-400 dark:text-gray-500 mt-1 ${isRTL ? 'text-right' : ''}`}>
                  {t(locale, 'usePreviousSettingsNote')}
                </p>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}

            <div className="border-t border-gray-100 dark:border-gray-800 pt-6" />

            {/* Action Buttons */}
            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                aria-disabled={!canSubmit || submitting}
                className="flex-1 bg-amber-200/80 hover:bg-amber-300 text-amber-900 font-bold py-3.5 rounded-xl transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-amber-800 border-t-transparent rounded-full animate-spin" />
                    {t(locale, 'loading')}
                  </span>
                ) : (
                  t(locale, 'reactivateInitiative')
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-8 py-3.5 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-slate-800 transition-all"
              >
                {t(locale, 'cancelAction')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
